import type { ClassLevelBase } from '@character-creator/types';
import { getClass } from '@character-creator/data';
import { calculateModifier } from './ability-scores';

/**
 * Multiclass spellcasting level weights
 */
const SPELLCASTING_LEVEL_WEIGHTS: Record<string, number> = {
  full: 1,
  half: 0.5,
  third: 1 / 3,
};

/**
 * Standard multiclass spell slots table
 * Index 0 = level 1, each sub-array is [1st, 2nd, 3rd, 4th, 5th, 6th, 7th, 8th, 9th]
 */
export const MULTICLASS_SPELL_SLOTS: number[][] = [
  [2, 0, 0, 0, 0, 0, 0, 0, 0], // 1
  [3, 0, 0, 0, 0, 0, 0, 0, 0], // 2
  [4, 2, 0, 0, 0, 0, 0, 0, 0], // 3
  [4, 3, 0, 0, 0, 0, 0, 0, 0], // 4
  [4, 3, 2, 0, 0, 0, 0, 0, 0], // 5
  [4, 3, 3, 0, 0, 0, 0, 0, 0], // 6
  [4, 3, 3, 1, 0, 0, 0, 0, 0], // 7
  [4, 3, 3, 2, 0, 0, 0, 0, 0], // 8
  [4, 3, 3, 3, 1, 0, 0, 0, 0], // 9
  [4, 3, 3, 3, 2, 0, 0, 0, 0], // 10
  [4, 3, 3, 3, 2, 1, 0, 0, 0], // 11
  [4, 3, 3, 3, 2, 1, 0, 0, 0], // 12
  [4, 3, 3, 3, 2, 1, 1, 0, 0], // 13
  [4, 3, 3, 3, 2, 1, 1, 0, 0], // 14
  [4, 3, 3, 3, 2, 1, 1, 1, 0], // 15
  [4, 3, 3, 3, 2, 1, 1, 1, 0], // 16
  [4, 3, 3, 3, 2, 1, 1, 1, 1], // 17
  [4, 3, 3, 3, 3, 1, 1, 1, 1], // 18
  [4, 3, 3, 3, 3, 2, 1, 1, 1], // 19
  [4, 3, 3, 3, 3, 2, 2, 1, 1], // 20
];

/**
 * Warlock pact magic slots table
 * Index 0 = level 1, values are [slot level, num slots]
 */
export const PACT_MAGIC_SLOTS: [number, number][] = [
  [1, 1], // 1
  [1, 2], // 2
  [2, 2], // 3
  [2, 2], // 4
  [3, 2], // 5
  [3, 2], // 6
  [4, 2], // 7
  [4, 2], // 8
  [5, 2], // 9
  [5, 2], // 10
  [5, 3], // 11
  [5, 3], // 12
  [5, 3], // 13
  [5, 3], // 14
  [5, 3], // 15
  [5, 3], // 16
  [5, 4], // 17
  [5, 4], // 18
  [5, 4], // 19
  [5, 4], // 20
];

export interface SpellSlots {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
  6: number;
  7: number;
  8: number;
  9: number;
}

export interface PactMagic {
  slotLevel: number;
  numSlots: number;
}

/**
 * Calculate multiclass spellcaster level
 * Full casters contribute their full level
 * Half casters contribute half (rounded down)
 * Third casters contribute a third (rounded down)
 */
export function calculateMulticlassSpellcasterLevel(classes: ClassLevelBase[]): number {
  let totalLevel = 0;

  for (const classLevel of classes) {
    const classData = getClass(classLevel.classId);
    if (!classData || !classData.spellcasting) continue;

    // Pact magic (Warlock) doesn't contribute to multiclass spell slots
    if (classData.spellcasting.type === 'pact') continue;

    const weight = SPELLCASTING_LEVEL_WEIGHTS[classData.spellcasting.type] || 0;
    totalLevel += Math.floor(classLevel.level * weight);
  }

  return totalLevel;
}

/**
 * Calculate spell slots for a character
 * Handles both single-class and multiclass scenarios
 */
export function calculateSpellSlots(classes: ClassLevelBase[]): SpellSlots {
  // Check if this is a single-class spellcaster
  const spellcastingClasses = classes.filter((c) => {
    const classData = getClass(c.classId);
    return classData?.spellcasting && classData.spellcasting.type !== 'pact';
  });

  if (spellcastingClasses.length === 0) {
    return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  }

  // Use multiclass spell slots table
  const casterLevel = calculateMulticlassSpellcasterLevel(classes);

  if (casterLevel === 0) {
    return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0, 8: 0, 9: 0 };
  }

  const slots = MULTICLASS_SPELL_SLOTS[Math.min(casterLevel, 20) - 1];

  return {
    1: slots[0],
    2: slots[1],
    3: slots[2],
    4: slots[3],
    5: slots[4],
    6: slots[5],
    7: slots[6],
    8: slots[7],
    9: slots[8],
  };
}

/**
 * Calculate pact magic for Warlocks
 */
export function calculatePactMagic(warlockLevel: number): PactMagic | null {
  if (warlockLevel < 1 || warlockLevel > 20) return null;

  const [slotLevel, numSlots] = PACT_MAGIC_SLOTS[warlockLevel - 1];
  return { slotLevel, numSlots };
}

/**
 * Calculate spell save DC
 */
export function calculateSpellSaveDC(
  spellcastingAbilityScore: number,
  proficiencyBonus: number
): number {
  return 8 + calculateModifier(spellcastingAbilityScore) + proficiencyBonus;
}

/**
 * Calculate spell attack bonus
 */
export function calculateSpellAttackBonus(
  spellcastingAbilityScore: number,
  proficiencyBonus: number
): number {
  return calculateModifier(spellcastingAbilityScore) + proficiencyBonus;
}

/**
 * Calculate number of cantrips known for a class at a given level
 */
export function getCantripsKnown(classId: string, level: number): number {
  const classData = getClass(classId);
  if (!classData?.spellcasting?.cantripsKnown) return 0;

  const cantrips = classData.spellcasting.cantripsKnown;
  return cantrips[Math.min(level, cantrips.length) - 1] || 0;
}

/**
 * Calculate number of spells known for a class at a given level
 * (for classes that learn spells, not prepare them)
 */
export function getSpellsKnown(classId: string, level: number): number {
  const classData = getClass(classId);
  if (!classData?.spellcasting?.spellsKnown) return 0;

  const spells = classData.spellcasting.spellsKnown;
  return spells[Math.min(level, spells.length) - 1] || 0;
}

/**
 * Get the Bardic Inspiration die size for a given Bard level.
 * d6 (1-4), d8 (5-9), d10 (10-14), d12 (15+)
 */
export function getBardicInspirationDie(bardLevel: number): string {
  if (bardLevel >= 15) return 'd12';
  if (bardLevel >= 10) return 'd10';
  if (bardLevel >= 5) return 'd8';
  return 'd6';
}

/**
 * Calculate maximum spell level a character can cast
 */
export function getMaxSpellLevel(classes: ClassLevelBase[]): number {
  const slots = calculateSpellSlots(classes);

  for (let level = 9; level >= 1; level--) {
    if (slots[level as keyof SpellSlots] > 0) {
      return level;
    }
  }

  // Check pact magic
  for (const classLevel of classes) {
    const classData = getClass(classLevel.classId);
    if (classData?.spellcasting?.type === 'pact') {
      const pactMagic = calculatePactMagic(classLevel.level);
      if (pactMagic) return pactMagic.slotLevel;
    }
  }

  return 0;
}
